import type { Notice } from "../services/api/types";
import { formatEventDisplayDate } from "./eventDateDisplay";
import { getPublishAfterEventStartError } from "./eventDateValidation";

export const PUBLISH_MIN_LEAD_ERROR =
  "Scheduled time must be at least 1 minute in the future";

export const INTERNAL_TEAM_ONLY_LABEL = "Internal team only";

/** Internal staff events are dashboard calendar entries — not app notices. */
export const STAFF_ONLY_SCHEDULE_ERROR =
  "Internal staff events cannot be scheduled for later publish";

export type StaffOnlyNoticeFormPatch = {
  publishMode: "now";
  publishAt: string;
  eventReminderModes: string[];
  eventReminderMode: "off";
  isUrgent: false;
};

export function applyStaffOnlyNoticeFormPatch(): StaffOnlyNoticeFormPatch {
  return {
    publishMode: "now",
    publishAt: "",
    eventReminderModes: [],
    eventReminderMode: "off",
    isUrgent: false,
  };
}

type FormChangeHandler = (event: {
  target: { name: string; value: unknown };
}) => void;

/** Clears schedule, reminders, and urgent flag when audience is internal staff only. */
export function applyStaffOnlyNoticeFormConstraints(
  handleChange: FormChangeHandler
): void {
  for (const [name, value] of Object.entries(applyStaffOnlyNoticeFormPatch())) {
    handleChange({ target: { name, value } });
  }
}

/** Notice types that may use publish-at scheduling (backend may enforce further rules). */
export function noticeTypeSupportsScheduledPublish(
  type: string | undefined
): boolean {
  return type !== "poll";
}

export type PublishAtValidationOptions = {
  eventStartDate?: Date | string | null;
  eventStartTime?: string;
};

export function getPublishAtValidationError(
  publishAt: string | undefined,
  options?: PublishAtValidationOptions
): string | undefined {
  if (!publishAt?.trim()) {
    return "Choose a publish date and time";
  }

  const publishMs = new Date(publishAt).getTime();
  if (Number.isNaN(publishMs) || publishMs <= Date.now() + 60_000) {
    return PUBLISH_MIN_LEAD_ERROR;
  }

  return getPublishAfterEventStartError(
    publishAt,
    options?.eventStartDate,
    options?.eventStartTime
  );
}

export function formatScheduleWhen(iso?: string): string {
  if (!iso) return "—";
  const { date, time } = parsePublishAtParts(iso);
  if (!date) {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return iso;
    return formatEventDisplayDate(parsed);
  }
  const dateStr = formatEventDisplayDate(date);
  return time ? `${dateStr} at ${time}` : dateStr;
}

export function parsePublishAtParts(publishAt?: string): {
  date: Date | null;
  time: string;
} {
  if (!publishAt?.trim()) {
    return { date: null, time: "" };
  }
  const parsed = new Date(publishAt);
  if (Number.isNaN(parsed.getTime())) {
    return { date: null, time: "" };
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: parsed,
    time: `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`,
  };
}

export function combinePublishAtParts(
  date: Date | null,
  time: string
): string {
  if (!date) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  const [hours, minutes] = (time || "09:00").split(":").map((part) => Number(part));
  const combined = new Date(date);
  combined.setHours(hours || 0, minutes || 0, 0, 0);
  return `${combined.getFullYear()}-${pad(combined.getMonth() + 1)}-${pad(combined.getDate())}T${pad(combined.getHours())}:${pad(combined.getMinutes())}`;
}

export function toDatetimeLocalValue(iso?: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function isStaffOnlyNotice(notice: Pick<Notice, "staffOnly">): boolean {
  return notice.staffOnly === true;
}

export function isScheduledNotice(
  notice: Pick<Notice, "publishStatus" | "publishAt">
): boolean {
  if (notice.publishStatus === "scheduled" && notice.publishAt) {
    return true;
  }
  if (!notice.publishAt || notice.publishStatus === "published") {
    return false;
  }
  const publishMs = new Date(notice.publishAt).getTime();
  return !Number.isNaN(publishMs) && publishMs > Date.now();
}

type DashboardUserForDelete = {
  userType?: string;
  username?: string;
  displayName?: string;
  fullName?: string;
} | null | undefined;

/** Match notice.createdBy against team username or display name. */
export function isNoticeCreatedByUser(
  notice: Pick<Notice, "createdBy" | "sender">,
  user: DashboardUserForDelete
): boolean {
  const createdBy = (notice.createdBy || "").trim();
  const sender = (notice.sender || "").trim();
  const candidates = [
    user?.username,
    user?.displayName,
    user?.fullName,
  ]
    .map((value) => (value || "").trim())
    .filter(Boolean);
  return candidates.some(
    (id) => (createdBy && createdBy === id) || (sender && sender === id)
  );
}

/** How long a team member has to delete a sent Notice they created, measured from createdAt. */
export const SENT_NOTICE_DELETE_WINDOW_MS = 30 * 60 * 1000;

/**
 * What Ask Noticeboard says about deleting a sent Notice. Same rule as
 * `canDashboardUserDeleteNotice`: an organization admin can always delete; a
 * team member can delete only a Notice they created, and only within
 * `SENT_NOTICE_DELETE_WINDOW_MS` of when it was created.
 */
export function describeNoticeDeleteRule(user: { userType?: string } | null | undefined): string {
  const minutes = SENT_NOTICE_DELETE_WINDOW_MS / 60000;
  if (user?.userType !== "team") {
    return `As an organization admin you can delete a sent Notice at any time. A Team member can delete only a Notice they created, within ${minutes} minutes of when it was created. After that, Duplicate a correction. Nothing is removed until you do that.`;
  }
  return `You can delete a sent Notice you created within ${minutes} minutes of when it was created. After that, Duplicate a correction. An organization admin can delete a Notice at any time. Nothing is removed until you do that.`;
}

/**
 * Org admins can always delete.
 * Creators can cancel a still-scheduled notice anytime.
 * Creators can delete a sent notice only within 30 minutes of create.
 */
export function canDashboardUserDeleteNotice(
  notice: Pick<
    Notice,
    "createdBy" | "sender" | "createdAt" | "publishStatus" | "publishAt"
  > | null | undefined,
  user: DashboardUserForDelete,
  nowMs: number = Date.now()
): boolean {
  if (!notice) return false;
  const isOrgAdmin = user?.userType !== "team";
  if (isOrgAdmin) return true;
  if (!isNoticeCreatedByUser(notice, user)) return false;
  if (isScheduledNotice(notice)) return true;
  const createdTime = new Date(notice.createdAt).getTime();
  if (Number.isNaN(createdTime)) return false;
  return nowMs - createdTime <= SENT_NOTICE_DELETE_WINDOW_MS;
}

/**
 * Who can change a sent Planner Event's date, time or location: an
 * organization admin always, a team member only for an Event they created.
 * Unlike deleting, there's no time limit, as moving an Event is routine.
 */
export function canDashboardUserChangeEvent(
  notice: Pick<Notice, "createdBy" | "sender"> | null | undefined,
  user: DashboardUserForDelete,
): boolean {
  if (!notice) return false;
  return user?.userType !== "team" || isNoticeCreatedByUser(notice, user);
}

export function isUpcomingScheduledNotice(
  notice: Pick<Notice, "publishStatus" | "publishAt" | "isDraft">,
  now: number = Date.now()
): boolean {
  if (notice.isDraft) return false;
  if (!isScheduledNotice(notice) || !notice.publishAt) return false;
  const publishMs = new Date(notice.publishAt).getTime();
  return !Number.isNaN(publishMs) && publishMs > now;
}

export function formatScheduleWhenShort(iso?: string): string {
  if (!iso) return "—";
  const { date, time } = parsePublishAtParts(iso);
  if (!date) {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return iso;
    return formatEventDisplayDate(parsed);
  }
  const dateStr = formatEventDisplayDate(date);
  return time ? `${dateStr} at ${time}` : dateStr;
}

export function getUpcomingScheduledNotices(
  notices: Notice[] | undefined | null,
  now: number = Date.now()
): Notice[] {
  return (notices || [])
    .filter((notice) => isUpcomingScheduledNotice(notice, now))
    .sort(
      (a, b) =>
        new Date(a.publishAt || 0).getTime() -
        new Date(b.publishAt || 0).getTime()
    );
}

function coerceNoticeDateTime(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return new Date(value).toISOString();
  }
  return undefined;
}

/** Feed sort/display time: scheduled notices use publishAt once live. */
export function resolveNoticeFeedCreatedAt(
  notice: Pick<Notice, "createdAt" | "publishAt" | "publishStatus">
): string {
  const createdAt = coerceNoticeDateTime(notice.createdAt) || "";
  const publishAt = coerceNoticeDateTime(notice.publishAt);
  if (!publishAt || notice.publishStatus === "scheduled") {
    return createdAt;
  }
  const createdMs = new Date(createdAt).getTime();
  const publishMs = new Date(publishAt).getTime();
  if (Number.isNaN(publishMs)) {
    return createdAt;
  }
  if (Number.isNaN(createdMs) || publishMs > createdMs) {
    return publishAt;
  }
  return createdAt;
}
